'use client'

import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'

import { ScrollReveal } from '@/shared/components/motion/ScrollReveal'

type HairLossTypesCarouselProps = {
  cta: { href: string }
}

// Code-owned "types of hair loss" carousel for the hair loss landing page. A
// scroll-snap track of four close-up photo tiles plus a final call-to-action
// tile that bleeds off the right edge. Desktop shows prev/next arrows beside
// the headline; mobile shows a dot indicator under the track. The copy and
// photos are hardcoded on purpose.
const imageRoot = '/images/landing-redesign/hair-loss'

const content = {
  titleBold: 'Whatever yours looks like,',
  titleLight: "there's a treatment for it.",
  types: [
    {
      key: 'receding',
      title: 'Receding hairline',
      description:
        'The temples creep back first. Usually the earliest sign, and the easiest to slow.',
      photo: `${imageRoot}/type-receding.webp`,
      alt: 'Close-up of a man’s receding temple in hard side light',
      position: 'object-[50%_45%]',
    },
    {
      key: 'crown',
      title: 'Thinning crown',
      description:
        "The patch you only see in photos. Treatable while there's still hair to keep.",
      photo: `${imageRoot}/type-crown.webp`,
      alt: 'The back of a man’s head with a thinning crown',
      position: 'object-[50%_35%]',
    },
    {
      key: 'thinning',
      title: 'Thinning all over',
      description:
        'Less density, more scalp under bright light. Slow enough that most men miss it.',
      photo: `${imageRoot}/type-thinning.webp`,
      alt: 'A man running his hand through thinning hair, seen from above',
      position: 'object-[50%_35%]',
    },
    {
      key: 'family',
      title: 'Runs in the family',
      description: 'Genetics set the pattern. Treatment changes the pace.',
      photo: `${imageRoot}/type-family.webp`,
      alt: 'A father and son from behind, both thinning at the crown',
      position: 'object-[50%_30%]',
    },
  ],
  cta: {
    label: 'Check eligibility',
    texture: `${imageRoot}/type-cta-silk.webp`,
  },
}

const slideCount = content.types.length + 1

export function HairLossTypesCarousel({ cta }: HairLossTypesCarouselProps) {
  const trackRef = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState(0)

  const stride = useCallback(() => {
    const track = trackRef.current
    const first = track?.firstElementChild as HTMLElement | null
    const second = first?.nextElementSibling as HTMLElement | null
    if (!first) return 0
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
  }, [])

  const handleScroll = useCallback(() => {
    const track = trackRef.current
    const step = stride()
    if (!track || step <= 0) return
    const next = Math.min(
      slideCount - 1,
      Math.max(0, Math.round(track.scrollLeft / step)),
    )
    setActive((current) => (current === next ? current : next))
  }, [stride])

  const scrollBy = useCallback(
    (direction: -1 | 1) => {
      const track = trackRef.current
      const step = stride()
      if (!track || step <= 0) return
      track.scrollBy({ left: direction * step, behavior: 'smooth' })
    },
    [stride],
  )

  const atStart = active === 0
  const atEnd = active >= slideCount - 1

  return (
    <section className="mx-auto max-w-[1440px] overflow-hidden pb-14 pt-[52px] xl:pl-[140px] xl:pt-[110px] xl:pb-[110px]">
      <ScrollReveal
        className="flex items-end justify-between px-5 xl:pr-[140px] xl:pl-0"
        distance={12}
      >
        <h2 className="font-jakarta text-[28px] leading-[34px] tracking-[-0.04em] xl:text-[44px] xl:leading-[50px]">
          <span className="block font-extrabold">{content.titleBold}</span>
          <span className="block font-extralight tracking-[-0.02em]">
            {content.titleLight}
          </span>
        </h2>
        <div className="hidden items-center gap-2.5 pb-1.5 xl:flex">
          <ArrowButton
            label="Previous"
            disabled={atStart}
            onClick={() => scrollBy(-1)}
          >
            <ChevronLeftIcon className="h-[22px] w-[22px]" />
          </ArrowButton>
          <ArrowButton
            label="Next"
            disabled={atEnd}
            onClick={() => scrollBy(1)}
          >
            <ChevronRightIcon className="h-[22px] w-[22px]" />
          </ArrowButton>
        </div>
      </ScrollReveal>

      <ul
        ref={trackRef}
        onScroll={handleScroll}
        className="mt-7 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-pl-5 px-5 [scrollbar-width:none] xl:mt-12 xl:gap-5 xl:scroll-pl-0 xl:px-0 xl:pr-[140px] [&::-webkit-scrollbar]:hidden"
      >
        {content.types.map((type) => (
          <li
            key={type.key}
            className="flex w-[240px] shrink-0 snap-start flex-col gap-3.5 xl:w-[260px] xl:gap-[18px]"
          >
            <div className="relative h-[300px] w-full overflow-hidden rounded-[20px] xl:h-[325px]">
              <Image
                src={type.photo}
                alt={type.alt}
                fill
                sizes="260px"
                className={`object-cover ${type.position}`}
              />
            </div>
            <div className="flex flex-col gap-1.5 xl:gap-2">
              <h3 className="font-jakarta text-[20px] font-bold leading-[26px] tracking-[-0.01em] xl:text-[22px] xl:leading-7">
                {type.title}
              </h3>
              <p className="text-[15px] leading-[22px] text-[#4A4A55] xl:text-base xl:leading-6">
                {type.description}
              </p>
            </div>
          </li>
        ))}
        <li
          className="w-[240px] shrink-0 snap-start xl:w-[260px]"
          aria-label={content.cta.label}
        >
          <div className="relative flex h-[300px] w-full flex-col justify-end overflow-hidden rounded-[20px] bg-[#DCE6F5] p-3.5 xl:h-[325px] xl:p-4">
            <Image
              src={content.cta.texture}
              alt=""
              fill
              sizes="260px"
              className="object-cover object-center"
            />
            <Link
              href={cta.href}
              className="relative flex h-[60px] items-center justify-between rounded-[18px] border border-white/60 bg-white/55 pl-[18px] pr-2 text-[17px] font-semibold leading-[22px] text-[#0B0B0F] backdrop-blur-[18px] transition-colors duration-200 hover:bg-white/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              {content.cta.label}
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0B0B0F]"
                aria-hidden="true"
              >
                <ArrowRightIcon className="h-[18px] w-[18px] text-white" />
              </span>
            </Link>
          </div>
        </li>
      </ul>

      <ol
        className="mt-6 flex items-center justify-center gap-1.5 xl:hidden"
        aria-hidden="true"
      >
        {Array.from({ length: slideCount }, (_, index) => (
          <li
            key={index}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
              index === active ? 'w-[18px] bg-[#0B0B0F]' : 'w-1.5 bg-[#D6D6DB]'
            }`}
          />
        ))}
      </ol>
    </section>
  )
}

function ArrowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-disabled={disabled}
      onClick={onClick}
      className={`flex h-11 w-11 items-center justify-center rounded-full bg-[#F4F4F6] transition-colors duration-200 hover:bg-[#ECECEF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB] ${
        disabled ? 'text-[#A0A0A8]' : 'text-[#0B0B0F]'
      }`}
    >
      {children}
    </button>
  )
}
